import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Loader2, Grid, List, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import ProductCard from '../components/ProductCard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ShopPage = () => {
  const { category } = useParams();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    category: category || '',
    device: '',
    material: '',
    minPrice: '',
    maxPrice: '',
    sortBy: 'created_at',
    sortOrder: 'desc'
  });
  const [viewMode, setViewMode] = useState('grid');

  useEffect(() => {
    fetchProducts();
  }, [category, filters]);

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.category || category) params.append('category', category || filters.category);
      if (filters.device) params.append('device', filters.device);
      if (filters.material) params.append('material', filters.material);
      if (filters.minPrice) params.append('min_price', filters.minPrice);
      if (filters.maxPrice) params.append('max_price', filters.maxPrice);
      params.append('sort_by', filters.sortBy);
      params.append('sort_order', filters.sortOrder);
      params.append('limit', '50');

      const response = await axios.get(`${API}/products?${params.toString()}`);
      setProducts(response.data.products);
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
    }
  };

  const getCategoryTitle = () => {
    const categoryMap = {
      'iphone-cases': 'iPhone Cases',
      'samsung-cases': 'Samsung Cases',
      'accessories': 'Accessories',
      'new-arrivals': 'New Arrivals',
      'best-sellers': 'Best Sellers',
      'sale': 'Sale'
    };
    return categoryMap[category] || 'All Products';
  };

  return (
    <div className="min-h-screen bg-stone-50" data-testid="shop-page">
      {/* Page Header with Kilim Pattern */}
      <div className="bg-stone-900 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10"
          style={{
            backgroundImage: `
              repeating-linear-gradient(45deg, transparent, transparent 40px, #fbbf24 40px, #fbbf24 42px),
              repeating-linear-gradient(-45deg, transparent, transparent 40px, #dc2626 40px, #dc2626 42px)
            `
          }}
        ></div>
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-12 relative z-10">
          <div className="flex items-center gap-3 mb-4">
            <Sparkles className="h-5 w-5 text-amber-400" />
            <span className="text-amber-400 text-sm tracking-[0.2em] uppercase">Shop Collection</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-black text-white mb-2" data-testid="category-title">{getCategoryTitle()}</h1>
          <p className="text-stone-400">Premium phone covers with bohemian luxury design</p>
        </div>
        {/* Bottom Kilim Border */}
        <div className="h-2"
          style={{
            backgroundImage: `repeating-linear-gradient(90deg, 
              #78350f 0px, #78350f 30px, 
              #dc2626 30px, #dc2626 60px, 
              #f59e0b 60px, #f59e0b 90px)`
          }}
        ></div>
      </div>

      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Filters & Sort */}
        <div className="bg-white shadow-lg p-6 mb-8 border-l-4 border-amber-600" data-testid="filters-section">
          <div className="flex flex-wrap items-center gap-4">
            {/* Device Filter */}
            <Select value={filters.device || "all"} onValueChange={(value) => setFilters({...filters, device: value === "all" ? "" : value})}>
              <SelectTrigger className="w-[200px] rounded-none border-stone-300 focus:border-amber-500">
                <SelectValue placeholder="Device" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Devices</SelectItem>
                <SelectItem value="iPhone 15">iPhone 15</SelectItem>
                <SelectItem value="iPhone 15 Pro">iPhone 15 Pro</SelectItem>
                <SelectItem value="iPhone 16">iPhone 16</SelectItem>
                <SelectItem value="Samsung S24">Samsung S24</SelectItem>
                <SelectItem value="Samsung S25">Samsung S25</SelectItem>
              </SelectContent>
            </Select>

            {/* Material Filter */}
            <Select value={filters.material || "all"} onValueChange={(value) => setFilters({...filters, material: value === "all" ? "" : value})}>
              <SelectTrigger className="w-[200px] rounded-none border-stone-300 focus:border-amber-500">
                <SelectValue placeholder="Material" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Materials</SelectItem>
                <SelectItem value="Leather">Leather</SelectItem>
                <SelectItem value="Silicone">Silicone</SelectItem>
                <SelectItem value="Clear">Clear</SelectItem>
                <SelectItem value="Carbon Fiber">Carbon Fiber</SelectItem>
              </SelectContent>
            </Select>

            {/* Sort */}
            <Select value={filters.sortBy} onValueChange={(value) => setFilters({...filters, sortBy: value})}>
              <SelectTrigger className="w-[200px] rounded-none border-stone-300 focus:border-amber-500">
                <SelectValue placeholder="Sort by" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="created_at">Newest</SelectItem>
                <SelectItem value="price">Price</SelectItem>
                <SelectItem value="rating">Rating</SelectItem>
              </SelectContent>
            </Select>

            {/* View Mode */}
            <div className="ml-auto flex gap-2">
              <Button
                variant={viewMode === 'grid' ? 'default' : 'outline'}
                size="icon"
                onClick={() => setViewMode('grid')}
                className={`rounded-none ${viewMode === 'grid' ? 'bg-amber-600 hover:bg-amber-700' : ''}`}
                data-testid="grid-view-btn"
              >
                <Grid className="h-4 w-4" />
              </Button>
              <Button
                variant={viewMode === 'list' ? 'default' : 'outline'}
                size="icon"
                onClick={() => setViewMode('list')}
                className={`rounded-none ${viewMode === 'list' ? 'bg-amber-600 hover:bg-amber-700' : ''}`}
                data-testid="list-view-btn"
              >
                <List className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>

        {/* Products Grid */}
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="h-12 w-12 animate-spin text-amber-600" />
          </div>
        ) : products.length === 0 ? (
          <div className="text-center py-20 bg-white shadow-lg">
            <Sparkles className="h-16 w-16 text-stone-300 mx-auto mb-4" />
            <p className="text-2xl text-stone-600 mb-4">No products found</p>
            <Link to="/shop/iphone-cases">
              <Button className="bg-amber-600 hover:bg-amber-700 rounded-none">Browse All Products</Button>
            </Link>
          </div>
        ) : (
          <div className={`grid gap-6 ${
            viewMode === 'grid'
              ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4'
              : 'grid-cols-1'
          }`} data-testid="products-grid">
            {products.map((product) => (
              <ProductCard key={product._id} product={product} viewMode={viewMode} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ShopPage;
